import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { getText } from '../utils/translations';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static texts for this page
const pageTexts = {
  joinUs: { en: 'Join Us', fr: 'Rejoignez-Nous', ar: 'انضم إلينا' },
  title: { en: 'Become a Partner', fr: 'Devenir Partenaire', ar: 'كن شريكاً' },
  subtitle: {
    en: 'Join our network of trusted partners and grow your business with world-class brands.',
    fr: 'Rejoignez notre réseau de partenaires de confiance et développez votre activité avec des marques de classe mondiale.',
    ar: 'انضم إلى شبكة شركائنا الموثوقين وطور أعمالك مع علامات تجارية عالمية المستوى.'
  },
  form: {
    companyName: { en: 'Company Name', fr: 'Raison Sociale', ar: 'اسم الشركة' },
    fullName: { en: 'Full Name', fr: 'Nom et Prénom', ar: 'الاسم الكامل' },
    address: { en: 'Address', fr: 'Adresse', ar: 'العنوان' },
    email: { en: 'Email', fr: 'Email', ar: 'البريد الإلكتروني' },
    phone: { en: 'Phone', fr: 'Téléphone', ar: 'الهاتف' },
    capital: { en: 'Capital', fr: 'Capital', ar: 'رأس المال' },
    message: { en: 'Additional Information', fr: 'Informations Supplémentaires', ar: 'معلومات إضافية' },
    submit: { en: 'Submit Application', fr: 'Envoyer la Demande', ar: 'إرسال الطلب' },
    sending: { en: 'Sending...', fr: 'Envoi en cours...', ar: 'جاري الإرسال...' },
    success: { en: 'Your information has been sent successfully!', fr: 'Vos informations ont été envoyées avec succès!', ar: 'تم إرسال معلوماتك بنجاح!' },
    error: { en: 'Failed to send. Please try again.', fr: 'Échec de l\'envoi. Veuillez réessayer.', ar: 'فشل الإرسال. حاول مرة أخرى.' }
  }
};

const BecomePartnerPage = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    company_name: '',
    full_name: '',
    address: '',
    email: '',
    phone: '',
    capital: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [recaptchaSettings, setRecaptchaSettings] = useState({ enabled: false, site_key: '' });
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const txt = pageTexts;

  // Fetch reCAPTCHA settings
  useEffect(() => {
    const fetchRecaptchaSettings = async () => {
      try {
        const response = await axios.get(`${API}/recaptcha-settings`);
        if (response.data) {
          setRecaptchaSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching reCAPTCHA settings:', error);
      }
    };
    fetchRecaptchaSettings();
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check reCAPTCHA if enabled
    if (recaptchaSettings.enabled && recaptchaSettings.site_key && !recaptchaToken) {
      toast.error(getText(language, 'Please complete the reCAPTCHA', 'Veuillez compléter le reCAPTCHA', 'يرجى إكمال اختبار reCAPTCHA'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/become-partner`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      toast.success(getText(language, txt.form.success.en, txt.form.success.fr, txt.form.success.ar));
      setFormData({
        company_name: '',
        full_name: '',
        address: '',
        email: '',
        phone: '',
        capital: '',
        message: ''
      });
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || getText(language, txt.form.error.en, txt.form.error.fr, txt.form.error.ar);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.joinUs.en, txt.joinUs.fr, txt.joinUs.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800 mb-6">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
          <p className="text-slate-500 max-w-xl">
            {getText(language, txt.subtitle.en, txt.subtitle.fr, txt.subtitle.ar)}
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-sm text-slate-600">
                    {getText(language, txt.form.companyName.en, txt.form.companyName.fr, txt.form.companyName.ar)}
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-sm text-slate-600">
                    {getText(language, txt.form.fullName.en, txt.form.fullName.fr, txt.form.fullName.ar)}
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-sm text-slate-600">
                  {getText(language, txt.form.address.en, txt.form.address.fr, txt.form.address.ar)}
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-slate-600">
                    {getText(language, txt.form.email.en, txt.form.email.fr, txt.form.email.ar)}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm text-slate-600">
                    {getText(language, txt.form.phone.en, txt.form.phone.fr, txt.form.phone.ar)}
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="capital" className="text-sm text-slate-600">
                  {getText(language, txt.form.capital.en, txt.form.capital.fr, txt.form.capital.ar)}
                </Label>
                <Input
                  id="capital"
                  name="capital"
                  value={formData.capital}
                  onChange={handleChange}
                  className="h-12 border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm text-slate-600">
                  {getText(language, txt.form.message.en, txt.form.message.fr, txt.form.message.ar)}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="border-slate-200 focus:border-slate-400 rounded-lg bg-slate-50"
                />
              </div>

              {/* reCAPTCHA */}
              {recaptchaSettings.enabled && recaptchaSettings.site_key && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSettings.site_key}
                    onChange={onRecaptchaChange}
                    hl={language === 'ar' ? 'ar' : language === 'fr' ? 'fr' : 'en'}
                  />
                </div>
              )}

              <Button 
                type="submit" 
                disabled={loading || (recaptchaSettings.enabled && recaptchaSettings.site_key && !recaptchaToken)}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white h-12 rounded-full"
              >
                {loading 
                  ? getText(language, txt.form.sending.en, txt.form.sending.fr, txt.form.sending.ar)
                  : getText(language, txt.form.submit.en, txt.form.submit.fr, txt.form.submit.ar)
                }
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BecomePartnerPage;
